import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import ProfilePicture from '@/components/ProfilePicture';
import { ArrowLeft, Users2, Loader2 } from 'lucide-react';
import { HomeworkAttachment as Attachment } from '@/components/HomeworkAttachment';

const dateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Shared homework for one coaching section, on its own page so the section itself stays clean. */
export default function SectionHomeworkFeed() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [sectionName, setSectionName] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sections/${folderId}/homework/shared`);
      setSubmissions(r.data.submissions || []);
      setSectionName(r.data.section_name || '');
      setAnonymous(r.data.anonymous_for_viewer !== false);
    } catch (e) {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl mx-auto py-6 px-4" data-testid="homework-feed-page">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-4 text-cyan-300 hover:text-cyan-200 -ml-2"
        data-testid="homework-feed-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to the section
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
          <Users2 className="w-6 h-6 text-cyan-400" /> Shared homework
        </h1>
        {sectionName && <p className="text-base text-cyan-200/80 mt-1">{sectionName}</p>}
        <p className="text-sm text-gray-300 mt-3 leading-relaxed">
          Work members chose to share with the group.
          {anonymous
            ? ' Names are never shown here — read it, learn from it, and take what is useful.'
            : ' You can see names because you review this section.'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-300 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center" data-testid="homework-feed-empty">
          <p className="text-gray-200">Nothing has been shared in this section yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Be the first — submit your homework and switch on &quot;share with the group&quot;.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((s) => (
            <div
              key={s.id}
              className={`bg-white rounded-xl p-5 border ${s.is_mine ? 'border-cyan-400 ring-1 ring-cyan-200' : 'border-gray-200'}`}
              data-testid={`feed-submission-${s.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {!anonymous && <ProfilePicture src={s.user_avatar} alt={s.username} size="xs" />}
                  {s.is_mine ? (
                    <span className="text-xs font-medium text-cyan-700" data-testid={`feed-submission-mine-${s.id}`}>
                      Your submission
                    </span>
                  ) : !anonymous ? (
                    <span className="text-xs font-medium text-gray-700">{s.username}</span>
                  ) : null}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{dateLabel(s.created_at)}</span>
              </div>
              {s.content && (
                <p className="text-[15px] text-gray-800 whitespace-pre-wrap leading-relaxed">{s.content}</p>
              )}
              <Attachment url={s.file_url} name={s.file_name} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
